// frontend/src/components/settings/ExactLocationResolveDialog.jsx
//
// The location gate's Resolve dialog. Unlike the item gate (thousands of rows
// → a search picker via the generic ResolveGateDialog), a store has a handful
// of locations, so each staged Exact warehouse gets ONE dropdown listing every
// Manexys location plus "Create new" and "Ignore for now". The backend's
// Code → Name cascade only preselects the dropdown — the merchant freely
// re-points it, which is how an auto-match is broken.
//
// Two invariants are enforced live in the picker and re-checked server-side:
//   * a location already chosen by another warehouse row is disabled, and
//   * a location already linked to a *different* warehouse (erp_id set) is
//     greyed with a reason.
// Together they keep the per-store warehouse ↔ location mapping one-to-one.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatLocationLabel } from '../../utils/itemLabel';
import { useIsShopify } from '../../contexts/AuthContext';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import { translateRequestError } from '../../queries/api';
import {
  useExactLocationSelection,
  useExactConfirmLocations,
} from '../../hooks/useConnectorMutations';

const CREATE = '__create__'; // import the warehouse as a new Manexys location
const IGNORE = '__ignore__'; // skip — leave it, it returns next synchronization
const locValue = (id) => `loc:${id}`;
// Shared so a dialog with no picks yet keeps one identity across renders.
const NO_PICKS = {};

// Backend-derived default for a freshly opened dialog: the auto-matched
// location, else create-new when the warehouse has a code to create from,
// else ignore (no code → nothing to create).
const defaultChoice = (r) => {
  if (r.match_location_id) return locValue(r.match_location_id);
  if (r.code) return CREATE;
  return IGNORE;
};

export default function ExactLocationResolveDialog({ connectorId, open, onClose, onConfirmed }) {
  const { t } = useTranslation();
  // A store can run Shopify as its sales channel AND Exact as its ERP, and the
  // options here are Manexys locations — whose code is a Shopify GID suffix on
  // such a store.
  const isShopify = useIsShopify();
  const { data, isPending, error } = useExactLocationSelection(connectorId, open);
  const confirmMut = useExactConfirmLocations(connectorId);

  // Per-row dropdown overrides on top of the backend defaults, keyed by
  // connectorId so re-opening starts clean without an effect.
  const [picks, setPicks] = useState({ key: null, map: {} });
  const [confirmError, setConfirmError] = useState(null);

  const rows = useMemo(() => data?.locations || [], [data]);
  const available = useMemo(() => data?.available_locations || [], [data]);

  const map = picks.key === connectorId ? picks.map : NO_PICKS;
  const choiceOf = useCallback((r) => map[r.erp_id] ?? defaultChoice(r), [map]);
  const setChoice = (erpId, value) => setPicks((prev) => {
    const next = prev.key === connectorId ? { ...prev.map } : {};
    next[erpId] = value;
    return { key: connectorId, map: next };
  });

  // location_id (string) → erp_id of the row currently claiming it, so a
  // location picked in one row greys out in every other row.
  const claimedBy = useMemo(() => {
    const claims = {};
    rows.forEach((r) => {
      const c = choiceOf(r);
      if (c.startsWith('loc:')) claims[c.slice(4)] = r.erp_id;
    });
    return claims;
  }, [rows, choiceOf]);

  const linkCount = rows.filter((r) => choiceOf(r).startsWith('loc:')).length;
  const createCount = rows.filter((r) => choiceOf(r) === CREATE).length;

  const handleConfirm = async () => {
    setConfirmError(null);
    const actions = {};
    for (const r of rows) {
      const c = choiceOf(r);
      if (c.startsWith('loc:')) actions[r.erp_id] = { action: 'link', location_id: Number(c.slice(4)) };
      else if (c === CREATE) actions[r.erp_id] = { action: 'import' };
      else actions[r.erp_id] = { action: 'skip' };
    }
    try {
      const resp = await confirmMut.mutateAsync(actions);
      onConfirmed?.(resp?.summary);
      onClose?.();
    } catch (err) {
      setConfirmError(translateRequestError(err, t, 'connectors.locationConfirmFailed'));
    }
  };

  const renderSelect = (r) => {
    const value = choiceOf(r);
    return (
      <FormControl size="small" fullWidth>
        <Select
          value={value}
          onChange={(e) => setChoice(r.erp_id, e.target.value)}
        >
          {r.code && <MenuItem value={CREATE}>{t('connectors.locationOptionCreate')}</MenuItem>}
          <MenuItem value={IGNORE}>{t('connectors.locationOptionIgnore')}</MenuItem>
          {!r.code && (
            <MenuItem disabled value="__nocode__" sx={{ whiteSpace: 'normal', fontStyle: 'italic' }}>
              {t('connectors.locationNoCodeHint')}
            </MenuItem>
          )}
          <Divider />
          <ListSubheader>{t('connectors.locationOptionsHeader')}</ListSubheader>
          {available.map((loc) => {
            const v = locValue(loc.location_id);
            const linkedElsewhere = loc.linked_erp_id && loc.linked_erp_id !== r.erp_id;
            const claimedByOther = claimedBy[loc.location_id]
              && claimedBy[loc.location_id] !== r.erp_id;
            const label = formatLocationLabel(loc, isShopify);
            return (
              <MenuItem key={loc.location_id} value={v} disabled={linkedElsewhere || claimedByOther}>
                {linkedElsewhere
                  ? t('connectors.locationOptionLinkedElsewhere', { label })
                  : claimedByOther
                    ? t('connectors.locationOptionTakenHere', { label })
                    : label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form dialog-form--wide">
      <DialogTitle>{t('connectors.locationResolveTitle')}</DialogTitle>
      <DialogContent>
        <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {t('connectors.locationResolveHelp')}
        </p>

        {isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={24} /></div>
        ) : error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.resolveLoadFailed')}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            {data?.gate_pending === false
              ? t('connectors.locationResolveEmptyConfirmed')
              : t('connectors.locationResolveEmpty')}
          </Alert>
        ) : (
          <>
            <Alert severity="info">
              {t('connectors.locationSummary', { linked: linkCount, create: createCount, total: rows.length })}
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('connectors.locationColCode')}</TableCell>
                  <TableCell>{t('connectors.locationColName')}</TableCell>
                  <TableCell sx={{ width: '45%' }}>{t('connectors.locationColMaps')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.erp_id} hover>
                    <TableCell>{r.code || '—'}</TableCell>
                    <TableCell>
                      {r.name || r.erp_id}
                      {r.is_main && (
                        <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={t('connectors.locationMainBadge')} />
                      )}
                    </TableCell>
                    <TableCell>{renderSelect(r)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogErrorBanner error={confirmError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmMut.isPending}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isPending || rows.length === 0}
          loading={confirmMut.isPending}
        >
          {createCount > 0
            ? t('connectors.locationConfirmCounts', { linked: linkCount, imported: createCount })
            : t('connectors.locationConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
